import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FiAlertTriangle, FiArrowRight, FiCheck, FiCheckCircle } from "react-icons/fi";

import { api, getOverview, OVERVIEW_REFRESH_MS } from "@/lib/api";
import { fmtDbm } from "@/lib/signal";
import { fmtNum, fmtTime, tStatus, useI18n, type Lang } from "@/lib/i18n";
import { incidentTarget, incidentTitle } from "@/lib/incident";
import type { Alert, Overview } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useIntegrations } from "@/components/MonitoringBanner";
import { integrationHealth, posture } from "@/lib/integrations";

type Health = { status: string; version: string; time: string };
type Tone = "good" | "warn" | "crit" | "idle";

const TEXT: Record<Tone, string> = {
  good: "text-signal-good",
  warn: "text-signal-warn",
  crit: "text-signal-crit",
  idle: "text-slate-100",
};
const BG: Record<Tone, string> = {
  good: "bg-signal-good",
  warn: "bg-signal-warn",
  crit: "bg-signal-crit",
  idle: "bg-slate-600",
};
const TARGET_ROUTES: Record<string, string> = { ont: "/onts", olt: "/olts" };

const delay = (i: number): CSSProperties => ({ animationDelay: `${i * 60}ms` });

/** "11h 43m": units as the NOC already writes them; jam/hari in Indonesian. */
function duration(iso: string, lang: Lang): string {
  const [h, d] = lang === "id" ? ["j", "h"] : ["h", "d"];
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}${h} ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}${d} ${hrs % 24}${h}`;
}

export default function OverviewPage() {
  const { t, locale } = useI18n();
  const { data, error, isLoading } = useQuery({
    queryKey: ["overview"],
    queryFn: getOverview,
    refetchInterval: OVERVIEW_REFRESH_MS,
  });

  if (isLoading) return <p className="text-slate-500">{t("common.loading")}</p>;
  if (!data) {
    return (
      <p className="flex items-center gap-2 text-rose-300">
        <FiAlertTriangle /> {(error as Error | null)?.message ?? t("common.error")}
      </p>
    );
  }

  const tot = data.totals;
  const affected = tot.onts_offline + tot.onts_missing;
  const health = tot.onts ? (tot.onts_online / tot.onts) * 100 : null;
  // ponytail: fixed 99/95 bands; move to Settings beside the signal bands if ops wants them tunable.
  const healthTone: Tone = health === null ? "idle" : health >= 99 ? "good" : health >= 95 ? "warn" : "crit";

  return (
    <div className="mx-auto max-w-6xl space-y-7">
      <header className="rise flex items-end justify-between gap-4">
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("overview.title")}</h1>
        <span className="data text-[11px] text-slate-500">{fmtTime(locale, data.generated_at)}</span>
      </header>

      {/* One instrument bar, not five cards: readouts share rules like a rack panel. */}
      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule lg:grid-cols-5">
        <Readout i={1} label={t("overview.health")} tone={healthTone} hint={t("overview.healthHint")}>
          {health === null ? "—" : `${health.toFixed(1)}%`}
          <span className="absolute inset-x-0 bottom-0 h-[2px] bg-rule">
            <span className={cn("block h-full", BG[healthTone])} style={{ width: `${health ?? 0}%` }} />
          </span>
        </Readout>
        <Readout
          i={2}
          label="OLT"
          to="/olts"
          tone={tot.olts_reachable < tot.olts ? "crit" : "idle"}
        >
          {fmtNum(locale, tot.olts_reachable)}
          <Of>{fmtNum(locale, tot.olts)}</Of>
        </Readout>
        <Readout i={3} label="ONT" to="/onts" tone={affected ? "warn" : "idle"}>
          {fmtNum(locale, tot.onts_online)}
          <Of>{fmtNum(locale, tot.onts)}</Of>
        </Readout>
        <Readout i={4} label={t("overview.affected")} tone={affected ? "crit" : "idle"} hint={t("overview.affectedHint")}>
          {fmtNum(locale, affected)}
        </Readout>
        <Readout i={5} label={t("overview.incidents")} to="/alerts" className="col-span-2 lg:col-span-1">
          <span className="flex items-baseline gap-4">
            <SevCount tone="crit" n={tot.alerts_critical} />
            <SevCount tone="warn" n={tot.alerts_warn} />
          </span>
        </Readout>
      </section>

      <Incidents alerts={data.alerts_open} olts={data.olts} total={tot.alerts_open} />

      <NetworkStatus olts={[tot.olts_reachable, tot.olts]} />
    </div>
  );
}

function Readout({
  i, label, hint, tone = "idle", to, className, children,
}: {
  i: number; label: string; hint?: string; tone?: Tone; to?: string; className?: string; children: ReactNode;
}) {
  const body = (
    <>
      <span className="eyebrow flex items-center gap-1.5">
        {label}
        {to && <FiArrowRight className="opacity-0 transition-opacity group-hover:opacity-100" />}
      </span>
      <span className={cn("data mt-2 block text-[2rem] font-medium leading-none tracking-tight", TEXT[tone])}>
        {children}
      </span>
      {hint && <span className="mt-2 block text-[11px] text-slate-500">{hint}</span>}
    </>
  );
  const cls = cn("rise group relative block bg-panel px-5 py-4", className);
  return to ? (
    <Link to={to} className={cn(cls, "transition-colors hover:bg-[#0d1321]")} style={delay(i)}>{body}</Link>
  ) : (
    <div className={cls} style={delay(i)}>{body}</div>
  );
}

const Of = ({ children }: { children: ReactNode }) => (
  <span className="text-base text-slate-600"> / {children}</span>
);

function SevCount({ tone, n }: { tone: Tone; n: number }) {
  return (
    <span className={cn("inline-flex items-baseline gap-2", n ? TEXT[tone] : "text-slate-600")}>
      <span className={cn("size-2 self-center rounded-full", n ? BG[tone] : "bg-slate-700", n && tone === "crit" && "beacon")} />
      {n}
    </span>
  );
}

function Incidents({ alerts, olts, total }: { alerts: Alert[]; olts: Overview["olts"]; total: number }) {
  const { t, lang, locale } = useI18n();
  const client = useQueryClient();
  const ack = useMutation({
    mutationFn: (id: number) => api(`/api/v1/alerts/${id}/ack`, { method: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["overview"] }),
  });

  return (
    <section className="rise" style={delay(6)}>
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="eyebrow">
          {t("overview.activeIncidents")} <span className="data ml-1 text-slate-400">{fmtNum(locale, total)}</span>
        </h2>
        <Link to="/alerts" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-signal">
          {t("overview.viewAll")} <FiArrowRight />
        </Link>
      </div>

      {alerts.length === 0 ? (
        <div className="flex items-center gap-3 rounded-lg border border-rule bg-panel px-5 py-8 text-signal-good">
          <FiCheckCircle className="size-5" />
          <span className="text-sm">{t("overview.noIncidents")}</span>
        </div>
      ) : (
        <ul className="divide-y divide-rule overflow-hidden rounded-lg border border-rule bg-panel">
          {alerts.map((a) => {
            const tone: Tone = a.severity === "critical" ? "crit" : a.severity === "warn" ? "warn" : "idle";
            const title = incidentTitle(t, a);
            const reading = a.metric.endsWith("_dbm") ? fmtDbm(a.value) : null;
            const route = TARGET_ROUTES[a.target_type];
            const acked = a.status === "ack";
            return (
              <li
                key={a.id}
                className={cn(
                  "relative grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-3.5 pl-6 pr-5 transition-colors hover:bg-white/[0.015]",
                  acked && "opacity-60",
                )}
              >
                <span
                  className={cn("absolute inset-y-3 left-0 w-[3px] rounded-r", BG[tone], tone === "crit" && !acked && "beacon", TEXT[tone])}
                  aria-hidden
                />
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-3">
                  <span className="sr-only">{tStatus(t, a.severity)}</span>
                  <span className="font-medium text-slate-100">{title}</span>
                  {reading && <span className={cn("data text-[13px]", TEXT[tone])}>{reading}</span>}
                  {acked && <span className="eyebrow text-amber-300/80">{tStatus(t, "acknowledged")}</span>}
                </div>
                <span className="data text-right text-[13px] text-slate-300" title={a.opened_at}>
                  {duration(a.opened_at, lang)}
                </span>

                <div className="min-w-0 truncate text-[12.5px] text-slate-400">
                  <span className="text-slate-300">{incidentTarget(a, olts)}</span>
                  {a.target_place && <span className="data text-[11.5px] text-slate-500"> · {a.target_place}</span>}
                  {a.metric === "ont.run_state" && <span className="text-slate-500"> · {t("overview.customerOffline")}</span>}
                </div>
                <div className="flex items-center justify-end gap-4 text-xs">
                  {!acked && (
                    <button
                      type="button"
                      disabled={ack.isPending}
                      onClick={() => ack.mutate(a.id)}
                      className="eyebrow inline-flex items-center gap-1 text-slate-300 transition-colors hover:text-signal disabled:opacity-40"
                    >
                      <FiCheck /> {t("alerts.ack")}
                    </button>
                  )}
                  {route && (
                    <Link
                      to={`${route}/${a.target_id}`}
                      className="eyebrow inline-flex items-center gap-1 text-slate-300 transition-colors hover:text-signal"
                    >
                      {t("overview.inspect")} <FiArrowRight />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {ack.isError && <p className="mt-2 text-xs text-rose-300">{(ack.error as Error).message}</p>}
    </section>
  );
}

function NetworkStatus({ olts: [up, all] }: { olts: [number, number] }) {
  const { t, locale } = useI18n();
  const health = useQuery({
    queryKey: ["health-banner"],
    queryFn: () => api<Health>("/health"),
    refetchInterval: 60_000,
  });
  // Same health rules as the Integrations page: an old OK run is stale, not
  // healthy. Disabled integrations are a choice, not a fault.
  const ints = useIntegrations();
  const hs = (ints.data?.integrations ?? []).map((r) => integrationHealth(r, Date.now()));
  const { posture: p, counts } = posture(hs);
  const live = hs.length - counts.disabled;
  const ok = counts.healthy + counts.running;

  return (
    <section className="rise" style={delay(7)}>
      <h2 className="eyebrow mb-3">{t("overview.networkStatus")}</h2>
      <div className="flex flex-wrap gap-x-10 gap-y-3 rounded-lg border border-rule bg-panel px-5 py-3.5">
        <StatusItem label="OLT" to="/olts" tone={up < all ? "crit" : "good"}>
          {fmtNum(locale, up)}/{fmtNum(locale, all)}
        </StatusItem>
        <StatusItem label={t("overview.collectors")} to="/integrations" tone={!ints.data ? "idle" : p === "failure" ? "crit" : p === "degraded" ? "warn" : "good"}>
          {ints.data ? `${fmtNum(locale, ok)}/${fmtNum(locale, live)}` : "…"}
        </StatusItem>
        <StatusItem
          label={t("overview.api")}
          tone={health.error ? "crit" : health.data ? "good" : "idle"}
          title={health.data?.version}
        >
          {health.error ? t("overview.unreachable") : health.data ? t("overview.healthy") : "…"}
        </StatusItem>
      </div>
    </section>
  );
}

function StatusItem({
  label, tone, to, title, children,
}: {
  label: string; tone: Tone; to?: string; title?: string; children: ReactNode;
}) {
  const body = (
    <>
      <span className="eyebrow">{label}</span>
      <span className={cn("size-1.5 rounded-full", BG[tone])} />
      <span className="data text-[13px] text-slate-200">{children}</span>
    </>
  );
  const cls = "inline-flex items-center gap-2.5";
  return to ? (
    <Link to={to} title={title} className={cn(cls, "hover:[&>.data]:text-signal")}>{body}</Link>
  ) : (
    <span title={title} className={cls}>{body}</span>
  );
}
